import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { getAllUsers, deleteUser, updateUserStatus } from '../services/api';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import FormUsuario from './FormUsuario';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  Chip,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SortableTh from './common/SortableTh';
import { useClientSort } from '../hooks/useClientSort';
import { sxLinhaInativa } from './common/StatusAtivo';
import { getApiErrorMessage } from '../utils/apiError';

function readPermissoes() {
  try {
    return JSON.parse(localStorage.getItem('permissoes') || 'null');
  } catch {
    return null;
  }
}

function UserList() {
  const permissions = readPermissoes();
  const canManage =
    permissions == null || Boolean(permissions.gerenciaUsuarios);
  const [usuarios, setUsuarios] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [form, setForm] = useState({ open: false, id: null });

  const fetchData = useCallback(async () => {
    try {
      const response = await getAllUsers();
      setUsuarios(response.data);
    } catch {
      setSnackbarMessage('Erro ao carregar usuários.');
      setAlertSeverity('error');
      setOpenSnackbar(true);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const userAccessors = useMemo(() => ({
    grupo: (u) => u.perfil,
    filiais: (u) => u.estabelecimentosResumo || (u.acessoTodasFiliais ? 'Todas' : ''),
    status: (u) => (u.ativo ? 1 : 0),
  }), []);
  const { sorted: usuariosOrdenados, orderBy, order, requestSort } = useClientSort(
    usuarios,
    'nome',
    'asc',
    userAccessors,
  );

  const handleOpenDialog = (id) => {
    setDeleteId(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDeleteId(null);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteUser(deleteId);
      setUsuarios(usuarios.filter((usuario) => usuario.id !== deleteId));
      setSnackbarMessage('Usuário excluído com sucesso!');
      setAlertSeverity('success');
    } catch (error) {
      setSnackbarMessage(getApiErrorMessage(error, 'Não foi possível excluir o usuário.'));
      setAlertSeverity('error');
    } finally {
      setOpenSnackbar(true);
      handleCloseDialog();
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const abrirForm = (id = null) => setForm({ open: true, id });
  const fecharForm = () => setForm((f) => ({ ...f, open: false }));
  const aoSalvar = (msg) => {
    fecharForm();
    setSnackbarMessage(msg);
    setAlertSeverity('success');
    setOpenSnackbar(true);
    fetchData();
  };

  const handleStatus = async (usuario) => {
    try {
      const ativo = !usuario.ativo;
      await updateUserStatus(usuario.id, ativo);
      setUsuarios((prev) =>
        prev.map((item) => (item.id === usuario.id ? { ...item, ativo } : item))
      );
      setSnackbarMessage(ativo ? 'Usuário ativado.' : 'Usuário inativado.');
      setAlertSeverity('success');
    } catch (error) {
      setSnackbarMessage(getApiErrorMessage(error, 'Não foi possível alterar o status.'));
      setAlertSeverity('error');
    } finally {
      setOpenSnackbar(true);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Usuários"
        subtitle="Operadores do PDV e do ZenGestão, com grupo de permissões e status."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirForm()}>
              Novo usuário
            </Button>
          )
        }
      />
      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTh id="nome" label="Nome" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="email" label="Email" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="telefone" label="Telefone" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="grupo" label="Grupo" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="filiais" label="Filiais" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="status" label="Status" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usuarios.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum usuário cadastrado
                </TableCell>
              </TableRow>
            )}
            {usuariosOrdenados.map((usuario) => (
              <TableRow
                key={usuario.id}
                hover
                sx={{ cursor: canManage ? 'pointer' : 'default', ...sxLinhaInativa(usuario.ativo) }}
                onClick={canManage ? () => abrirForm(usuario.id) : undefined}
              >
                <TableCell data-label="Nome">{usuario.nome}</TableCell>
                <TableCell data-label="Email">{usuario.email}</TableCell>
                <TableCell data-label="Telefone">{usuario.telefone}</TableCell>
                <TableCell data-label="Grupo">{usuario.perfil}</TableCell>
                <TableCell data-label="Filiais">
                  <Chip
                    size="small"
                    variant="outlined"
                    label={usuario.estabelecimentosResumo || (usuario.acessoTodasFiliais ? 'Todas' : '—')}
                  />
                </TableCell>
                <TableCell data-label="Status">
                  <Chip
                    size="small"
                    color={usuario.ativo ? 'success' : 'default'}
                    label={usuario.ativo ? 'Ativo' : 'Inativo'}
                  />
                </TableCell>
                <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                  {canManage && (
                    <>
                      <Switch
                        size="small"
                        checked={usuario.ativo}
                        onChange={() => handleStatus(usuario)}
                        inputProps={{ 'aria-label': `Status de ${usuario.nome}` }}
                      />
                      <IconButton color="primary" onClick={() => abrirForm(usuario.id)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleOpenDialog(usuario.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <EntityFormDialog
        open={form.open}
        title={form.id ? 'Editar usuário' : 'Novo usuário'}
        subtitle={form.id ? `Código ${form.id}` : null}
        onClose={fecharForm}
        maxWidth="md"
      >
        <FormUsuario
          key={form.id || 'novo'}
          embedded
          userId={form.id}
          onCancel={fecharForm}
          onSaved={aoSalvar}
        />
      </EntityFormDialog>

      {/* Diálogo de Confirmação */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Excluir Usuário</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza de que deseja excluir este usuário? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={alertSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default UserList;
